import {useState} from "react";
import {Modal} from "react-bootstrap";
import {useFormContext} from "react-hook-form";

import {Alert} from "@/components/alert/Alert.tsx";
import Button from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {TagsField} from "@/components/form/fields/TagsField/TagsField.tsx";
import {SelectServiceBoxes} from "@/components/form/SelectServiceBoxes.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {
  MonitoringServiceChoices,
  MonitoringServiceType,
  ServiceDefinition,
  ServiceGroup,
  ServiceTag,
} from "@/lib/types/checks.ts";
import {CheckFormAutoGeneratedField} from "@/pages/checks/components/form/fields/CheckFormAutoGeneratedField.tsx";
import {FIELD_TYPES} from "@/pages/checks/constants.ts";

interface CheckAggregationFormProps {
  serviceType: MonitoringServiceType;
  service: ServiceDefinition;
  mspChoices: MonitoringServiceChoices;
  monitoringServiceTypes: ServiceGroup[];
  tags: ServiceTag[];
}

export const CheckAggregationForm = ({
  serviceType,
  service,
  mspChoices,
  monitoringServiceTypes,
  tags,
}: CheckAggregationFormProps) => {
  const [subFormVisible, setSubFormVisible] = useState<boolean>(false);
  const [groupCheckServicesSavedValue, setGroupCheckServicesSavedValue] = useState<number[]>([]);
  const methods = useFormContext();
  const checkId = methods.watch(ServiceField.ID);
  const isAverageGroup =
    serviceType === MonitoringServiceType.GROUP &&
    methods.watch(ServiceField.GROUP_UPTIME_PERCENT_CALCULATION) === "AVERAGE";

  const serviceFieldsMap = service.fields.reduce(
    (acc, field) => {
      acc[field.field as ServiceField] = field;
      return acc;
    },
    {} as Record<ServiceField, (typeof service.fields)[number]>
  );

  return (
    <div className="pulseMaxWidthL">
      <div className="d-flex gap-4 gap-sm-3 flex-column flex-sm-row">
        <div className="col">
          <CheckFormAutoGeneratedField
            field={serviceFieldsMap[ServiceField.GROUP_CHECK_SERVICES]}
            versions={service.versions}
            serviceType={service.service_type}
            type={FIELD_TYPES[ServiceField.GROUP_CHECK_SERVICES]}
            mspChoices={mspChoices}
            toggleSubform={() => {
              setGroupCheckServicesSavedValue(methods.getValues(ServiceField.GROUP_CHECK_SERVICES) || []);
              setSubFormVisible(!subFormVisible);
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            }}
          />
          <Modal show={subFormVisible} onHide={() => setSubFormVisible(false)} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title className="pulseTextH2Bold pulse-color-gray-800 py-2">Select Checks</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <SelectServiceBoxes
                name={ServiceField.GROUP_CHECK_SERVICES}
                monitoringServiceTypes={monitoringServiceTypes.map(({group, types}) => ({
                  group,
                  types: types.filter(({name}) => name !== MonitoringServiceType.GROUP),
                }))}
                excludeMonitoringServiceType={MonitoringServiceType.GROUP}
                tags={tags}
                excludeIds={checkId ? [checkId] : []}
                skipRequest={!subFormVisible}
              />
              <Divider className="my-3" />
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    methods.setValue(ServiceField.GROUP_CHECK_SERVICES, groupCheckServicesSavedValue);
                    setSubFormVisible(false);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={() => setSubFormVisible(false)}>Save</Button>
              </div>
            </Modal.Body>
          </Modal>
        </div>
        <div className="pulseMaxWidthS col">
          <TagsField
            name={ServiceField.GROUP_CHECK_TAGS}
            label="Auto-select Checks by Tag"
            tooltip="Include all present or future Checks with any of the selected Tags."
            tags={tags}
          />
        </div>
      </div>
      <div className="mt-4">
        <>
          <CheckFormAutoGeneratedField
            field={serviceFieldsMap[ServiceField.GROUP_UPTIME_PERCENT_CALCULATION]}
            versions={service.versions}
            serviceType={service.service_type}
            type={FIELD_TYPES[ServiceField.GROUP_UPTIME_PERCENT_CALCULATION]}
            mspChoices={mspChoices}
          />
          {isAverageGroup && (
            <Alert
              callout="Attention"
              variant="maintenance"
              className="mb-0 mt-3"
              children={
                <span>
                  No Alerts are issued when you{" "}
                  <span className="pulseTextBodyBold">Report on the group’s average uptime %</span>. Switch to{" "}
                  <span className="pulseTextBodyBold">Alert when Downtime conditions are met</span> to receive Alerts
                  for this Group Check.
                </span>
              }
            />
          )}
        </>
      </div>
      <div className="mt-4">
        <CheckFormAutoGeneratedField
          field={serviceFieldsMap[ServiceField.GROUP_CHECK_DOWN_CONDITION]}
          versions={service.versions}
          serviceType={service.service_type}
          type={FIELD_TYPES[ServiceField.GROUP_CHECK_DOWN_CONDITION]}
          mspChoices={mspChoices}
        />
      </div>
      <div className="mt-4">
        <CheckFormAutoGeneratedField
          field={serviceFieldsMap[ServiceField.GROUP_RESPONSE_TIME_CALCULATION_MODE]}
          versions={service.versions}
          serviceType={service.service_type}
          type={FIELD_TYPES[ServiceField.GROUP_RESPONSE_TIME_CALCULATION_MODE]}
          mspChoices={mspChoices}
        />
      </div>
    </div>
  );
};
